import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isOnline, onNetworkChange } from '@/lib/network';
import { failedCount, onOutboxChange, onOutboxFailed, pendingCount } from '@/lib/outbox';

/**
 * Полоса состояния связи.
 *
 * ЗАЧЕМ. Без неё приложение без сети выглядит сломанным: экраны показывают
 * сохранённые данные, но человек не понимает, почему ничего не обновляется и
 * куда делись его ответы. Полоса отвечает на оба вопроса разом — «связи нет» и
 * «столько-то ответов ждут отправки».
 *
 * ПОЧЕМУ ПОЛОСА, А НЕ ВСПЛЫВАЮЩЕЕ СООБЩЕНИЕ. Отсутствие связи — это состояние,
 * а не событие: оно длится, и напоминать о нём нужно всё время, пока оно есть.
 * Всплывающее сообщение пропадает через пару секунд и оставляет человека
 * в неведении.
 *
 * Всплывашка «Нет соединения» (`components/ui/NetworkToast.tsx`, 25.09.2026) полосу НЕ заменяет — она
 * про событие: конкретный запрос не дошёл, в том числе когда система считает, что связь есть.
 *
 * Когда связь вернулась, а неотправленное осталось, полоса не исчезает сразу:
 * показывает, что очередь разгружается. Пропадает, когда всё ушло.
 *
 * ⚠️ ОТКАЗ СЕРВЕРА — СРАЗУ (10.09.2026). Ответ, который сервер не примет
 * никогда, раньше неделю числился «ждёт отправки», и ученик думал, что он уйдёт.
 * Теперь такие ответы считаются отдельно и полоса показывает их тут же, красным.
 */
export function OfflineBar() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(isOnline);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(0);

  useEffect(() => {
    void pendingCount().then(setPending);
    void failedCount().then(setFailed);
    const offNet = onNetworkChange(setOnline);
    const offBox = onOutboxChange(setPending);
    const offFailed = onOutboxFailed(setFailed);
    return () => {
      offNet();
      offBox();
      offFailed();
    };
  }, []);

  if (online && pending === 0 && failed === 0) return null;

  const offline = !online;
  const tone =
    failed > 0
      ? 'bg-error-container text-on-error-container'
      : offline
        ? 'bg-warning-container text-on-warning-container'
        : 'bg-surface-container-highest text-on-surface-variant';
  const icon = failed > 0 ? 'error_outline' : offline ? 'cloud_off' : 'cloud_sync';
  const waiting = offline || pending > 0;

  return (
    // ⚠️ translate="no" и текст только внутри <span> — от переводчика браузера
    // (техдолг 263): голый текст рядом с появляющимися соседями роняет кабинет.
    <div
      role="status"
      aria-live="polite"
      // Под полоской нативный ролик урока обрезается (ветка video-native-webview) — `lib/inlineVideoNative.ts`.
      data-native-video-cover=""
      translate="no"
      className={'safe-inset sticky top-0 z-40 flex flex-wrap items-center justify-center gap-2 px-4 py-2 text-xs font-semibold ' + tone}
    >
      <span className="material-symbols-outlined text-base">{icon}</span>
      {waiting && <span>{offline ? t('net.offline') : t('net.syncing')}</span>}
      {pending > 0 && <span className="opacity-80">· {t('net.pending', { count: pending })}</span>}
      {failed > 0 && (
        // ⚠️ Одной строкой, а не `{waiting ? '· ' : ''}{…}`: пустая строка у React —
        // не узел, и «· » удалялся при смене `waiting` — под переводчиком это тот же
        // «removeChild» (поймано translatorCrash.test.ts, 24.09.2026).
        <span>{(waiting ? '· ' : '') + t('net.failed', { count: failed })}</span>
      )}
    </div>
  );
}
